<script setup lang="ts">
import type { DropdownMenuItem } from '@nuxt/ui';
import type { ResolvedMonospacePrimitiveField } from '#layers/data-model/types/schema/resolved-schema';
import { useClipboard, usePermission } from '@vueuse/core';
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { useFieldNullable } from '#layers/block-engine';
import { showConfirmationDialog } from '#layers/ui/utils/show-confirmation-dialog';
import { parseRawValue, serializeRawValue } from '~/utils/raw-value';
import FieldLabelEditDialog from './FieldLabelEditDialog.vue';

const props = defineProps<{
	label: string; // Used for aria-label, not rendered
	primitiveField?: ResolvedMonospacePrimitiveField;
	disabled?: boolean;
}>();

const model = defineModel<unknown>();

const nullable = useFieldNullable(() => props.primitiveField);

const { t } = useI18n();
const { copy, copied, text } = useClipboard({ read: true });
const clipboardReadPermission = usePermission('clipboard-read');
const clipboardWritePermission = usePermission('clipboard-write');

const menuItems = computed<DropdownMenuItem[][]>(() => {
	const items: DropdownMenuItem[] = [
		{
			label: t('common.edit_raw_value'),
			icon: 'lucide:code',
			onSelect: openEditModal,
		},
		{
			label: copied.value ? t('common.copied') : t('common.copy_raw_value'),
			icon: copied.value ? 'lucide:check' : 'lucide:copy',
			onSelect: (e) => {
				copyValue();
				e.preventDefault();
			},
			disabled: clipboardWritePermission.value === 'denied',
		},
		{
			label: t('common.paste_raw_value'),
			icon: 'lucide:clipboard-paste',
			disabled: clipboardReadPermission.value === 'denied' || props.disabled,
			onSelect: pasteValue,
		},
	];

	if (nullable.value) {
		items.push({
			label: t('common.clear_value'),
			icon: 'lucide:trash-2',
			onSelect: clearValue,
		});
	}

	return [items];
});

function copyValue() {
	copy(props.primitiveField?.type ? serializeRawValue(model.value, props.primitiveField.type) : String(model.value));
}

function pasteValue() {
	model.value = props.primitiveField?.type ? parseRawValue(text.value, props.primitiveField.type) : text.value;
}

async function openEditModal() {
	const result = await showConfirmationDialog({
		component: FieldLabelEditDialog,
		props: {
			value: model.value,
			primitiveField: props.primitiveField,
			disabled: props.disabled,
		},
	});

	if (result) {
		model.value = result.value;
	}
}

function clearValue() {
	model.value = null;
}
</script>

<template>
	<DDropdownMenu :items="menuItems">
		<button
			type="button"
			class="group/trigger absolute inset-0 flex cursor-pointer items-center justify-end pe-0"
			:aria-label="$t('blocks.form_field.label_dropdown_trigger', { label })"
			tabindex="-1"
		>
			<DIcon
				name="lucide:chevron-down"
				class="size-3 text-muted opacity-0 group-hover:opacity-100 group-data-[state=open]/trigger:opacity-100"
			/>
		</button>
	</DDropdownMenu>
</template>
